import { uiText } from "@/lib/i18n/uiText";
import { Link } from "@/lib/router-compat";
import { Card } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, BarChart3, History, LineChart } from "lucide-react";

/**
 * Compact internal-link block wired into every historical match preview and
 * the /fixtures archive. It closes the loop between the three historical
 * surfaces search crawlers care about:
 *
 *   - back → /historical-odds  (context: how the odds moved)
 *   - forward → /match-preview (context: full preview grid)
 *   - forward → /analysis      (context: deep statistical analysis)
 *
 * `fixtureQuery` (optional) is appended to /match-preview and /analysis so a
 * click from a specific fixture lands on the pre-filtered view when the
 * destination page supports it, but the base links remain crawlable.
 */
export function HistoricalCrossLinks({
  fixtureQuery,
  className,
}: {
  fixtureQuery?: string;
  className?: string;
}) {
  const q = fixtureQuery ? `?q=${encodeURIComponent(fixtureQuery)}` : "";
  const items: {
    to: string;
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    direction: "back" | "forward";
  }[] = [
    {
      to: "/historical-odds",
      label: "Historical Odds Archive",
      description: "See how the odds moved before kick-off across bookmakers",
      icon: History,
      direction: "back",
    },
    {
      to: `/match-preview${q}`,
      label: "Full Match Preview",
      description: "Every market prediction, tips and confidence scores",
      icon: LineChart,
      direction: "forward",
    },
    {
      to: `/analysis${q}`,
      label: "Deep Match Analysis",
      description: "The statistical breakdown behind the prediction",
      icon: BarChart3,
      direction: "forward",
    },
  ];

  return (
    <nav aria-label={uiText("Related historical pages")} className={className}>
      <h2 className="mb-3 text-lg font-semibold">{uiText("Related historical pages")}</h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        {items.map((item) => (
          <Link key={item.to} to={item.to} className="group">
            <Card className="h-full p-4 transition-colors hover:border-primary/50">
              <div className="flex items-start gap-3">
                <div className="rounded-lg bg-primary/10 p-2">
                  <item.icon className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0">
                  <p className="flex items-center gap-1 text-sm font-semibold group-hover:text-primary">
                    {item.direction === "back" ? (
                      <ArrowLeft className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                    ) : null}
                    {uiText(item.label)}
                    {item.direction === "forward" ? (
                      <ArrowRight className="h-3.5 w-3.5 opacity-0 transition-opacity group-hover:opacity-100" />
                    ) : null}
                  </p>
                  <p className="mt-0.5 text-xs text-muted-foreground">{uiText(item.description)}</p>
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>
    </nav>
  );
}
